import { useState, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Excel/CSV import modal.
 * Props:
 *   entity: "customers" | "products"  – controls endpoints and labels
 *   open, onOpenChange, onImported()
 */
export default function ImportModal({ entity = "customers", open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const previewUrl = `/import/${entity}-preview`;
  const importUrl = `/import/${entity}`;
  const templateUrl = entity === "products"
    ? `${API_BASE}/product-master/import-template`
    : `${API_BASE}/import/${entity}-template`;
  const templateName = entity === "products"
    ? "TZAT_Product_Master_Template.xlsx"
    : `${entity}_template.csv`;
  const entityLabel = entity === "products" ? "Products" : "Customers";

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    const form = new FormData();
    form.append("file", f);
    setLoading(true);
    api.post(previewUrl, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Preview failed"))
      .finally(() => setLoading(false));
  };

  const confirmImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(importUrl, form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} ${entity} (${data.skipped} skipped)`);
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(templateUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = templateName;
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-heading">Import {entityLabel} from Excel / CSV</DialogTitle></DialogHeader>

        {!result && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn" className="border-[#3A4640]">
                <Download size={14} className="mr-2" /> Download Template
              </Button>
              <div className="text-xs text-[#8A8F98]">
                {entity === "customers" ? "Template includes your custom fields." : "Columns: Category*, Brand*, Model*, Variant, Product Name, Description, Notes, Active"}
              </div>
            </div>

            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-[#3A4640] rounded-md p-8 text-center cursor-pointer hover:border-[#39FF14]/40 hover:bg-[#0B0F0C] transition-colors"
              data-testid="import-dropzone"
            >
              <Upload size={32} className="mx-auto text-[#8A8F98] mb-2" />
              <div className="font-medium text-[#F8F9FA]">{file ? file.name : "Click to select or drag & drop"}</div>
              <div className="text-xs text-[#8A8F98] mt-1">.xlsx or .csv up to 5 MB</div>
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" data-testid="import-file-input" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
            </div>

            {preview && (
              <div className="mt-5 border border-[#3A4640] rounded-md overflow-hidden">
                <div className="bg-[#0B0F0C] border-b border-[#3A4640] px-4 py-2 text-xs uppercase tracking-wider font-heading font-semibold text-[#8A8F98]">
                  <FileSpreadsheet size={12} className="inline mr-1.5" /> Preview — {preview.total} row{preview.total === 1 ? "" : "s"} total, showing first {preview.sample.length}
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-[#121815] sticky top-0 border-b border-[#3A4640]">
                      <tr>{preview.columns.map((c) => <th key={c} className="text-left px-3 py-2 font-heading font-semibold text-[#F8F9FA]">{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.sample.map((r, i) => (
                        <tr key={i} className="border-b border-[#3A4640]">
                          {preview.columns.map((c) => <td key={c} className="px-3 py-2 text-[#F8F9FA]">{String(r[c] ?? "")}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#3A4640]">Cancel</Button>
              <Button onClick={confirmImport} disabled={!file || loading} data-testid="confirm-import-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                {loading ? "Importing…" : `Import ${preview?.total || 0} ${entity}`}
              </Button>
            </div>
          </>
        )}

        {result && (
          <div className="py-6 text-center">
            <CheckCircle2 size={48} className="text-[#39FF14] mx-auto mb-3" />
            <div className="font-heading text-2xl font-extrabold text-[#F8F9FA]">Import complete</div>
            <div className="text-[#8A8F98] mt-1">
              <span className="text-[#39FF14] font-semibold">{result.created}</span> added
              {result.skipped > 0 && <> · <span className="text-[#DC2626] font-semibold">{result.skipped}</span> skipped</>}
              {" "}out of {result.total} total
            </div>
            {result.errors.length > 0 && (
              <div className="mt-4 text-left border border-yellow-500/30 bg-yellow-500/10 rounded-md p-3 max-h-48 overflow-y-auto">
                <div className="flex items-center gap-2 text-yellow-300 text-xs font-heading font-semibold uppercase mb-2">
                  <AlertTriangle size={12} /> Skipped rows
                </div>
                <ul className="text-xs text-[#F8F9FA] space-y-1">
                  {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              </div>
            )}
            <Button onClick={() => onOpenChange(false)} className="mt-6 bg-[#39FF14] hover:bg-[#2ECC71]" data-testid="import-done-btn">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
